/**
 * A panel that slides over the page from one edge, on the native `<dialog>`.
 *
 * Same contract as `Modal`: `showModal()` gives the focus trap, Escape, the
 * top layer and the inert page behind. It differs in shape (a full-height
 * panel on an edge, not a centred box) and in staying mounted while closed, so
 * a caller can read from its children, such as a count, before it is opened.
 *
 * @module components/ui/Drawer
 */

import React, {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
} from "react";

import { IconButton } from "./Button";
import { EditLockExempt } from "./EditLock";
import { Heading } from "./Text";

export type DrawerSide = "start" | "end";

const SIDE_CLASS: Record<DrawerSide, string> = {
  start: "left-0 border-r",
  end: "right-0 border-l",
};

export interface DrawerProps {
  open: boolean;
  /** Called for every close path: the button, Escape, the backdrop. */
  onClose: () => void;
  title: ReactNode;
  /** Names the dialog when the visible title is not the whole of it. */
  "aria-label"?: string;
  /** Names the close button; defaults to "Close". */
  closeLabel?: string;
  /** The edge it opens from. */
  side?: DrawerSide;
  /** Where focus goes once it closes. Defaults to whatever held it on open. */
  returnFocusTo?: RefObject<HTMLElement | null>;
  /** `false` when the close sends the reader somewhere else, which owns focus. */
  restoreFocus?: boolean;
  children: ReactNode;
  "data-testid"?: string;
  /** Test id of the dimming backdrop, a pointer-only close. */
  backdropTestId?: string;
}

/**
 * @example
 * ```tsx
 * <Drawer open={open} onClose={close} title="Findings">
 *   <FindingsList />
 * </Drawer>
 * ```
 */
export function Drawer({
  open,
  onClose,
  title,
  "aria-label": ariaLabel = undefined,
  closeLabel = "Close",
  side = "end",
  returnFocusTo = undefined,
  restoreFocus = true,
  children,
  "data-testid": testId,
  backdropTestId = undefined,
}: DrawerProps): React.ReactElement {
  const ref = useRef<HTMLDialogElement>(null);
  const headingId = useId();
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) {
      return;
    }
    if (open && !dialog.open) {
      opener.current = document.activeElement;
      dialog.showModal();
      // The panel itself, not its first control, so a screen reader starts at
      // the top of what opened rather than on Close.
      dialog.focus();
    } else if (!open && dialog.open) {
      dialog.close();
      const target = returnFocusTo?.current ?? opener.current;
      if (restoreFocus && target instanceof HTMLElement) {
        target.focus();
      }
      opener.current = null;
    }
  }, [open, returnFocusTo, restoreFocus]);

  // Escape fires `cancel`; closing through state keeps the caller in charge.
  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      event.preventDefault();
      onClose();
    },
    [onClose],
  );

  return (
    <dialog
      ref={ref}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabel ? undefined : headingId}
      tabIndex={-1}
      className="fixed inset-0 m-0 h-full max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-transparent"
      onCancel={handleCancel}
      data-testid={testId}
    >
      {/* Pointer-only, as in `Modal`: the header's Close is the named control,
          and Escape routes through onCancel. */}
      <button
        type="button"
        className="absolute inset-0 bg-black/30"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        data-testid={backdropTestId}
      />
      <div
        className={`absolute top-0 flex h-full w-[min(420px,100vw)] flex-col gap-3 overflow-y-auto border-base-300 bg-base-100 p-4 shadow-2xl ${SIDE_CLASS[side]}`}
      >
        <div className="flex items-center justify-between gap-2">
          <Heading level={2} size="md" id={headingId}>
            {title}
          </Heading>
          <EditLockExempt>
            <IconButton icon="x" aria-label={closeLabel} onClick={onClose} />
          </EditLockExempt>
        </div>
        {children}
      </div>
    </dialog>
  );
}
